import { createSlice } from "@reduxjs/toolkit";
import { getGridInititalState } from "../../constants/dataGridConfig";

export const updateGridFilterModel =
  (gridName, filterKeysArr, gridApi) => (dispatch, getState) => {
    const { gridLayout } = getState();
    const gridSavedLayout = gridLayout.find(
      (gridLayout) => gridLayout.gridName === gridName
    );
    if (gridSavedLayout) {
      let filterModel = JSON.parse(JSON.stringify(gridSavedLayout.filterModel));
      let newFilter = {};
      filterKeysArr.forEach((filterKey) => {
        let value =
          typeof filterKey.value === "object"
            ? filterKey.value
            : [filterKey.value];
        newFilter[filterKey.name] = {
          values: value,
          filterType: "set",
          displayName: filterKey.displayName,
        };
        if (!filterModel[filterKey.name] && filterKey.value) {
          filterModel[filterKey.name] = newFilter[filterKey.name];
        } else if (filterKey.value) {
          filterModel[filterKey.name].values = value;
        } else {
          delete filterModel[filterKey.name];
        }
      });
      dispatch(
        saveFilterModel({
          gridName: gridName,
          filterModel,
        })
      );
    } else {
      let filterModel = {};
      filterKeysArr.forEach((filterKey) => {
        let value =
          typeof filterKey.value === "object"
            ? filterKey.value
            : [filterKey.value];
        if (filterKey.value) {
          filterModel[filterKey.name] = {
            values: value,
            filterType: "set",
            displayName: filterKey.displayName,
          };
        }
      });
      dispatch(
        initializeState(getGridInititalState(gridName, { filterModel }))
      );
    }
    if (gridApi) {
      const { gridLayout } = getState();
      const gridSavedLayout = gridLayout.find(
        (gridLayout) => gridLayout.gridName === gridName
      );
      gridApi.setFilterModel(gridSavedLayout.filterModel);
    }
  };

const gridLayoutSlice = createSlice({
  name: "gridLayout",
  initialState: [],
  reducers: {
    initializeState(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      if (!currentGrid) {
        state.push({
          gridName: action.payload.gridName,
          rowGroupKey: action.payload.gridConfig.rowGroupKey,
          filterModel: action.payload.gridConfig.filterModel,
          columnModel: action.payload.gridConfig.columnModel,
          selection: action.payload.gridConfig.selection,
          rowCount: action.payload.gridConfig.rowCount,
          rowData: action.payload.gridConfig.rowData,
          initialScrollPosition:
            action.payload.gridConfig.initialScrollPosition,
          hashes: {},
          hasChanges: false,
          toolPanel: {
            open: false,
            name: null,
          },
          rowGroup: {
            current: null,
            options: [],
          },
          advancedQueryPanel: {
            open: false,
            query: null,
            selected: null,
          },
          savedQueriesPanel: {
            open: false,
          },
        });
      } else {
        currentGrid.rowGroupKey = action.payload.gridConfig.rowGroupKey;
        currentGrid.filterModel = action.payload.gridConfig.filterModel;
        currentGrid.columnModel = action.payload.gridConfig.columnModel;
        currentGrid.selection = action.payload.gridConfig.selection;
        currentGrid.rowCount = action.payload.gridConfig.rowCount;
        currentGrid.rowData = action.payload.gridConfig.rowData;
        currentGrid.initialScrollPosition =
          action.payload.gridConfig.initialScrollPosition;
        currentGrid.hasChanges = false;
        currentGrid.hashes = {};
        currentGrid.toolPanel = {
          open: false,
          name: null,
        };
        currentGrid.rowGroup = {
          current: null,
          options: [],
        };
        currentGrid.advancedQueryPanel.open = false;
        currentGrid.advancedQueryPanel.selected = null;
        currentGrid.savedQueriesPanel.open = false;
      }
    },
    saveFilterModel(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );
      currentGrid.filterModel = action.payload.filterModel;
    },
    saveColumnModel(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );
      currentGrid.columnModel = action.payload.columnModel;
    },
    setRowCount(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );
      currentGrid.rowCount = action.payload.rowCount;
      //if totalRowCount is undefined, which means that the grid is loaded on the very first time
      //we set the the totalRowCount
      if (currentGrid.totalRowCount === undefined) {
        currentGrid.totalRowCount = action.payload.rowCount;
      }
      let hasFilters =
        currentGrid.filterModel &&
        Object.keys(currentGrid.filterModel).length > 0;
      //if filterModel is empty, which means that the grid is loaded with all rows
      //we set the the totalRowCount
      if (!hasFilters) {
        currentGrid.totalRowCount = action.payload.rowCount;
      }

      // Store total children count for tree data grids (e.g., total microskills)
      // Always update when provided (including when filters change)
      if (action.payload.totalChildrenCount !== undefined) {
        currentGrid.totalChildrenCount = action.payload.totalChildrenCount;
        // If in selectAll mode for tree data, update treeChildCount to match new totalChildrenCount
        // This handles the case when filters change and reduce the total available children
        if (currentGrid.selection.allSelected && currentGrid.selection.toggledNodes.length === 0) {
          currentGrid.selection.treeChildCount = action.payload.totalChildrenCount;
        }
      } else if (currentGrid.totalChildrenCount === undefined) {
        // Initialize to 0 if never set
        currentGrid.totalChildrenCount = 0;
      }
      
      //update selected count when grid row count request is finished
      currentGrid.selection.selectedCount = currentGrid.selection.allSelected
        ? currentGrid.rowCount -
          currentGrid.selection.toggledNodes.length -
          currentGrid.selection.unselectableRowCount
        : currentGrid.selection.toggledNodes.length;
    },
    setSelection(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      if (currentGrid) {
        currentGrid.selection.allSelected =
          action.payload.selection.selectAll ||
          action.payload.selection.selectAllChildren;
        //added toggledNodes property as default property of ag grid
        //toggledNodes property is used to store the selected rows or the deselected if selectAll is true
        currentGrid.selection.toggledNodes =
          action.payload.selection.toggledNodes ?? [];
        currentGrid.selection.filter = action.payload.selection.filter;
        
        // Store raw AG Grid selection state for tree data (used for restoration)
        if (action.payload.selection.rawSelectionState !== undefined) {
          currentGrid.selection.rawSelectionState = action.payload.selection.rawSelectionState;
        }
        
        // Store tree selection child count if provided (for tree data)
        // For selectAll mode with no deselections, always use totalChildrenCount as the source of truth
        // This ensures the count stays in sync when filters change
        const isSelectAllWithNoToggles = currentGrid.selection.allSelected && 
          (!currentGrid.selection.toggledNodes || currentGrid.selection.toggledNodes.length === 0);
        
        if (isSelectAllWithNoToggles && currentGrid.totalChildrenCount !== undefined) {
          currentGrid.selection.treeChildCount = currentGrid.totalChildrenCount;
        } else if (action.payload.selection.treeChildCount !== undefined) {
          currentGrid.selection.treeChildCount = action.payload.selection.treeChildCount;
        }
        
        //selected count is been calculated based on some conditions
        //if selectAll checkbox has been checked, then the selected count is the total row count minus the toggledNodes length
        //minus the unselectableRowCount
        //else the selected count is the toggledNodes length
        let selectedCount = currentGrid.selection.allSelected
          ? currentGrid.rowCount -
            currentGrid.selection.toggledNodes.length -
            currentGrid.selection.unselectableRowCount
          : currentGrid.selection.toggledNodes.length;
        currentGrid.selection.selectedCount = selectedCount;
      }
    },
    clearSelection(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      if (currentGrid) {
        currentGrid.selection.allSelected = false;
        currentGrid.selection.toggledNodes = [];
        currentGrid.selection.selectedCount = 0;
        currentGrid.selection.rawSelectionState = null;
        currentGrid.selection.treeChildCount = null;
      }
    },
    setUnselectableRowCount(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.selection.unselectableRowCount =
        action.payload.unselectableRowCount;
    },
    saveGroupState(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.rowGroupKey = action.payload.rowGroupKey;
    },
    updateRowData(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.rowData = action.payload.rowData;
      currentGrid.hasChanges = true;
    },
    deleteState(state, action) {
      return state.filter((x) => x.gridName !== action.payload.gridName);
    },
    setToolPanel(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.toolPanel = {
        open: action.payload.open ?? true,
        name: action.payload.name,
      };
    },
    setRowGroup(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.rowGroup = {
        current:
          action.payload.current === undefined
            ? currentGrid.rowGroup.current
            : action.payload.current,
        options:
          action.payload.options === undefined
            ? currentGrid.rowGroup.options
            : action.payload.options,
      };
    },
    setAdvancedQueryPanel(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.advancedQueryPanel = {
        open: action.payload.open,
        query: action.payload.query,
        name: action.payload.name,
      };
    },
    setSavedQueriesPanel(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.savedQueriesPanel = {
        open: action.payload.open,
      };
    },
    setSelectedQuery(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.advancedQueryPanel.open = true;
      currentGrid.advancedQueryPanel.selected = action.payload.selected;

      currentGrid.savedQueriesPanel.open = false;
    },
    saveScrollPosition(state, action) {
      let currentGrid = state.find(
        (grid) => grid.gridName === action.payload.gridName
      );

      currentGrid.initialScrollPosition = action.payload.scrollPosition;
    },
    setRequestHash: {
      prepare(payload) {
        return {
          payload: {
            timestamp: new Date().getTime(),
            ...payload,
          },
        };
      },
      reducer(state, action) {
        let currentGrid = state.find(
          (grid) => grid.gridName === action.payload.gridName
        );

        if (currentGrid)
          currentGrid.hashes[action.payload.hash] = {
            response: action.payload.response,
            timestamp: action.payload.timestamp,
          };
      },
    },
  },
});

export const {
  saveFilterModel,
  saveColumnModel,
  setRowCount,
  initializeState,
  setSelection,
  clearSelection,
  saveGroupState,
  updateRowData,
  deleteState,
  setToolPanel,
  setAdvancedQueryPanel,
  setSavedQueriesPanel,
  setSelectedQuery,
  setRowGroup,
  setRequestHash,
  saveScrollPosition,
  setUnselectableRowCount,
} = gridLayoutSlice.actions;
export default gridLayoutSlice.reducer;
